import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  ViewEncapsulation,
  effect,
  inject,
  signal
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FADE_IN_OUT_ANIMATION, SLIDE_UP_DOWN_ANIMATION } from '@partners/ui';
import { LottieComponent } from 'ngx-lottie';
import { IHttpLoaderOptions } from '../../http';
import { LoaderService } from '../services';

@Component({
  selector: 'application-loader',
  standalone: true,
  imports: [MatProgressSpinnerModule, LottieComponent],
  template: `
    @defer(when loader$$()?.animation) {
    <div class="loader-content">
      @if(loader$$()?.animation === 'default') {
      <mat-spinner
        [@fadeInOut]
        class="loader-spinner block"
        diameter="80"
        strokeWidth="2"
        color="#fff"
        mode="indeterminate"
      ></mat-spinner>
      } @else {
      <ng-lottie class="loader-animation block" width="100%" [options]="{ path: loader$$().animation }"></ng-lottie>
      } @if(loader$$()?.message) {
      <div class="loader-message text-center font--md font--medium">
        <span [@slideUpDown]>{{ loader$$().message }}</span>
      </div>
      }
    </div>
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [FADE_IN_OUT_ANIMATION, SLIDE_UP_DOWN_ANIMATION]
})
export class LoaderComponent implements OnDestroy {
  loader$$ = signal<IHttpLoaderOptions>(undefined);

  /**
   * whenever a new http loader configuration is announced, we trigger the loader spooling to
   * reflect the changes in animation (spinner|lottie) and messages. this effect will trigger
   * on every http call which requires a loading animation
   */
  #loaderEffectRef = effect(
    () => {
      /** read the most current loader configuration */
      const { options } = this.#loaderService.loaderState$$();

      /** trigger animation & message update */
      this.#spoolAnimations(options);
    },
    { allowSignalWrites: true }
  );

  #animInterval: number | undefined = undefined;

  /**
   * display a single or sequenced loader animation. "animations" arg always received
   * as an array. if it contains more then a single node - we have a sequence. in a sequence
   * each animation (and/or message) has a fixed amount of time (duration) to run - after
   * which the next animation is loaded (recursively)
   * @param animations an array of loader animation configuration
   */
  #spoolAnimations(animations: IHttpLoaderOptions[]): void {
    /** extract next animation config node */
    const nextAnimation = animations.shift();

    /**
     * duration is the primary arg for a sequence, omitted duration means no sequence!
     * just show animation spinner */
    if (!nextAnimation?.duration) {
      this.loader$$.set(nextAnimation);
      return;
    }

    /**
     * if we got here we are in the middle of sequence spooling. we first need to check
     * weather the recursive spooling needs to terminate
     */
    if (!animations?.length) {
      clearTimeout(this.#animInterval);
      return;
    }

    /** show next loaded animation */
    this.loader$$.set(nextAnimation);

    /** set a timer to load after current animation interval ends */
    this.#animInterval = setTimeout(() => {
      console.log(`spooling next animation...`);
      this.#spoolAnimations(animations);
    }, nextAnimation.duration);
  }

  @HostBinding('class.application-loader')
  hostClass = true;

  @HostBinding('[@fadeInOut]')
  hostAnimation = true;

  #loaderService = inject(LoaderService);

  constructor() {}

  ngOnDestroy(): void {
    this.#loaderEffectRef.destroy();
    /** clean house - again. just in case */
    clearTimeout(this.#animInterval);
  }
}
